import { useWebSocket, type WebSocketStatus } from '@vueuse/core';
import { globalKeys } from 'assets/data/global-keys';
import type { IWebSocketMessage } from '~/interfaces/notifications/notifications';
import { useNotifications } from '~/composables/useStates/useNotifications';

const webSocket = ref<ReturnType<typeof useWebSocket> | null>(null);

export default () => {
  const auth = useAuthStore();
  const { handleNewNotification } = useNotifications();
  const { $storageService } = useServices();
  const runtimeConfig = useRuntimeConfig();

  const uri = ref<string>(
    `${runtimeConfig.public.webSocketUri}?access_token=${auth.currentAccessToken || $storageService.getCookie({ key: globalKeys.authKeys.accessToken })}`,
  );

  const initWebSocket = async () => {
    if (
      webSocket.value?.status === 'CONNECTING' ||
      webSocket.value?.status === 'OPEN'
    )
      return;
    const debugEnabled = computed(
      () =>
        !!$storageService.getSessionStorage({
          key: globalKeys.developerConfigKeys.debugWebSocket,
        }) && import.meta.dev,
    );
    webSocket.value = useWebSocket(`${uri.value}`, {
      autoReconnect: false,
      heartbeat: {
        message: globalKeys.siteConfigKeys.webSocketPingMessage,
        interval: globalKeys.siteConfigKeys.webSocketPingInterval,
      },
      onConnected: (_) => {
        if (debugEnabled.value) console.log('WebSocket connected', _);
      },
      onDisconnected: (_, event) => {
        if (debugEnabled.value) console.log('WebSocket disconnected', _, event);
      },
      onError: (_, event) => console.error('WebSocket error:', _, event),
      onMessage: (_, event: MessageEvent<string>) => {
        const message: IWebSocketMessage =
          event.data === 'pong' ? null : JSON.parse(event.data);
        if (debugEnabled.value)
          console.log('WebSocket message received', message);
        if (message?.event === 'NotificationPanelMessage')
          handleNewNotification(message.data);
      },
    });
  };

  const disconnectFromWebSocket = async () => {
    if (!!webSocket && webSocket.value?.status === 'CLOSED') return;
    webSocket.value.close();
  };

  const ping = (message: string = 'ping') => {
    if (!!webSocket.value && webSocket.value?.status === 'OPEN')
      webSocket.value.send(message);
    else if (!!webSocket.value && webSocket.value?.status === 'CONNECTING')
      setTimeout(() => ping(message), 1000);
    else if (!!webSocket.value && webSocket.value?.status === 'CLOSED')
      initWebSocket().then(() => ping());
  };

  return {
    initWebSocket,
    data: computed(() => webSocket.value.data) as Readonly<Ref<unknown>>,
    disconnectFromWebSocket,
    ping,
    status: computed(() => webSocket.value.status) as Readonly<
      Ref<WebSocketStatus>
    >,
  };
};
